import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { NavItem, NavLink, TabPane, Nav, TabContent } from 'reactstrap';
import classnames from 'classnames';
import { Button } from '../../../../shared/components';
import {
  Topic,
  TopicStatus,
  Answer,
  MultipleChoiceWrapper,
} from '../../../../services/types/Toolkit';
import TopicRow from './TopicRow';
import QuestionsTable from './QuestionsTable';
import { safeFind } from '../../../../shared/misc/utils';

interface NavProps {
  answers: Answer[];
  topics: Topic[];
  handleInputChange: (
    value: number,
    questionId: number,
    topicId: number,
    multipleChoiceId: MultipleChoiceWrapper | null
  ) => void;
  toolkitCompleted: (completed: boolean) => void;
  isDraft: boolean;
  isEspf: boolean;
  handleTabChange: (topicNumber: number) => void;
}

const TopicsNavs = (props: NavProps) => {
  const { answers, topics, handleInputChange, toolkitCompleted, isDraft, isEspf, handleTabChange } =
    props;
  const { t } = useTranslation();
  const getTopicStatus = (topicId: number) => {
    let status = TopicStatus.Empty;
    const topicAnswers = answers.filter((x) => x.topicId === topicId);

    // patch for Lbr, question 1 (id: 72) disables rest of answers
    if (
      answers.some((x) => x.questionId === 72 && x.value === 1) ||
      answers.some((x) => x.questionId === 85 && x.value === 0) ||
      answers.some((x) => x.questionId === 67 && x.value === 0)
    ) {
      return TopicStatus.Full;
    }

    const topic = safeFind(topics, (x) => x.id === topicId);
    if (!topic.questions) {
      return status;
    }

    const questionAnswers = topicAnswers.filter((answer) => {
      if (answer.value !== undefined) {
        status = TopicStatus.Progress;

        const q = topic.questions ? topic.questions.find((x) => x.id === answer.questionId) : null;
        if (q && q.multipleChoices.length > 0 && answer.value >= q.minValueAllowMultipleChoices) {
          if (answer.answerMultipleChoices.length === 0) {
            return false;
          }

          const requiredComments = q.multipleChoices.filter((x) => x.commentsRequired);
          return !answer.answerMultipleChoices.find((x) =>
            requiredComments.find(
              (r) =>
                r.id === x.multipleChoiceId &&
                (x.comments === null || x.comments === undefined || x.comments === '')
            )
          );
        }

        return true;
      }
      return false;
    });

    if (questionAnswers.length === topic.questions.length) {
      status = TopicStatus.Full;
    }
    return status;
  };

  const getInititialTab = () => {
    const topic = topics.find((x) => getTopicStatus(x.id) !== TopicStatus.Full);
    return topic ? topic.number.toString() : '1';
  };

  const isCompleted = topics.every((topic) => getTopicStatus(topic.id) === TopicStatus.Full);
  useEffect(() => {
    toolkitCompleted(isCompleted);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCompleted]);
  const [activeTab, setActiveTab] = useState(() => getInititialTab());

  const getTopicTitle = (topic: Topic) => {
    if (topics.length === 1) {
      return `${topic.description}`;
    }

    const topicStr = topic.number < 10 ? `0${topic.number}` : topic.number.toString();
    return `${topicStr}. ${topic.description}`;
  };

  const toggle = (topicNumber: number) => {
    const tab = topicNumber.toString();
    if (activeTab !== tab) {
      handleTabChange(topicNumber);
      setActiveTab(tab);
    }
  };
  const nextTab = (topicNumber: number) => {
    if (topicNumber < topics.length) {
      handleTabChange(topicNumber);
      toggle(topicNumber + 1);
    }
  };
  const previousTab = (topicNumber: number) => {
    if (topicNumber > 1) {
      handleTabChange(topicNumber);
      toggle(topicNumber - 1);
    }
  };

  const renderTopicNavs = (topicList: Topic[]) =>
    topicList.map((topic: Topic) => (
      <NavItem key={topic.number.toString()}>
        <NavLink
          className={classnames({ active: activeTab === topic.number.toString() })}
          onClick={() => {
            toggle(topic.number);
          }}
          id={`nl_${topic.number}`}
        >
          <TopicRow topicTitle={getTopicTitle(topic)} status={getTopicStatus(topic.id)} />
        </NavLink>
      </NavItem>
    ));

  const renderTopicPanes = (topicList: Topic[]) =>
    topicList.map((topic: Topic) => (
      <TabPane key={topic.number.toString()} tabId={topic.number.toString()}>
        <QuestionsTable
          answers={answers}
          topic={topic}
          handleChange={(value, questionId, multipleChoice) =>
            handleInputChange(value, questionId, topic.id, multipleChoice)
          }
          isDraft={isDraft}
          isEspf={isEspf}
        />
        <div className="topic-navigator">
          <div className="float-left">
            {topic.number > 1 && (
              <Button
                className="btn-tertiary"
                type="button"
                text={t('topicPrevious')}
                onClick={() => previousTab(topic.number)}
                icon="chevron-left"
              />
            )}
          </div>
          <div className="float-right">
            {topic.number < topics.length && (
              <Button
                className="btn-tertiary"
                type="button"
                text={t('topicNext')}
                onClick={() => nextTab(topic.number)}
                iconRight="chevron-right"
              />
            )}
          </div>
        </div>
      </TabPane>
    ));
  return (
    <>
      <div className="toolkit-topics">
        <Nav className={topics.length < 2 ? 'no-overflow' : ''} tabs>
          {renderTopicNavs(topics)}
        </Nav>
      </div>
      <div style={{ width: '70%' }} className="toolkit-questions">
        <TabContent activeTab={activeTab}>{renderTopicPanes(topics)}</TabContent>
      </div>
    </>
  );
};

export default TopicsNavs;
